import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useId, useState } from "react";
import {
  HiOutlineArrowDownTray,
  HiOutlineEye,
  HiOutlinePencilSquare,
  HiOutlinePaperAirplane,
  HiOutlineShieldCheck,
} from "react-icons/hi2";

import { isApiClientError } from "../api/errors";
import type { DocumentWorkflowFileVersion } from "../entities/document-workflow";
import { useDocumentFiles } from "./documentFilesContext";
import { scanStatusView } from "./fileLabels";
import { SignedPreviewDialog } from "./FilePreviewDialog";
import { unavailableReasons } from "./signingMessages";
import {
  isGlobalReason,
  useSigningAvailability,
} from "./useSigningAvailability";
import { downloadErrorMessage, downloadVersionFile } from "./versionDownload";
import { VersionSignatures } from "./VersionSignatures";
import { VersionSigningPanel } from "./VersionSigningPanel";
import { Notice } from "./Notice";

function unlinkErrorMessage(error: unknown): string {
  if (
    isApiClientError(error) &&
    error.code === "document_workflow.immutable_version"
  ) {
    return "Подписанная версия неизменяема: отвязать её нельзя.";
  }
  if (isApiClientError(error) && error.status === 403) {
    return "Недостаточно прав, чтобы отвязать файл от документа.";
  }

  return "Не удалось отвязать версию. Обновите карточку и повторите попытку.";
}

/**
 * Действия над одной версией файла: открыть, скачать, подписать, подписи,
 * отвязка.
 *
 * Доступность решает сервер (`available_actions`) и состояние проверки:
 * версия вне `clean` не открывается и не скачивается — кнопок нет, а причина
 * названа текстом. Подпись недоступна, пока сервер не подтвердил, что она
 * включена; причина видна рядом, а не только после нажатия. Клиентские
 * проверки лишь повторяют серверные, а не заменяют их.
 */
export function VersionControls({
  version,
  displayName,
  withVersionInLabels = false,
}: {
  version: DocumentWorkflowFileVersion;
  displayName: string;
  /** В списке версий имена кнопок различаются номером версии. */
  withVersionInLabels?: boolean;
}) {
  const { services, canUnlink, signingNoticeId, autoSignVersionId } =
    useDocumentFiles();
  const client = useQueryClient();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [signaturesOpen, setSignaturesOpen] = useState(false);
  const [signOpen, setSignOpen] = useState(autoSignVersionId === version.id);
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const signaturesId = useId();
  const unlinkTitleId = useId();
  const signHintId = useId();

  const scan = scanStatusView(version.scan_status);
  const { availability, capabilities } = useSigningAvailability(version);
  const canOpen =
    scan.releasable && version.available_actions.includes("preview");
  const canDownload =
    scan.releasable && version.available_actions.includes("download");
  const signed = version.signature_state === "signed";
  const suffix = withVersionInLabels ? ` версию ${version.version_number}` : "";
  const label = (text: string) =>
    withVersionInLabels ? { "aria-label": `${text}${suffix}` } : {};

  const download = useMutation({
    mutationFn: () => downloadVersionFile(services, version, displayName),
  });
  const unlink = useMutation({
    mutationFn: () =>
      services.documentWorkflow.unlinkDocumentVersion(version.id),
    onSuccess: () => {
      setConfirmUnlink(false);
      return client.invalidateQueries({ queryKey: ["document-workflow"] });
    },
  });

  const unlinkable =
    canUnlink && version.provenance.type !== "manual" && !signed;
  const canStartSigning = availability.status === "available";
  // Общая причина показана один раз над списком, причина по версии — рядом с ней.
  const hint =
    availability.status === "unavailable" &&
    availability.reason === "version_not_ready"
      ? unavailableReasons.version_not_ready
      : availability.status === "checking"
        ? "Проверяем, включена ли электронная подпись…"
        : null;
  const describedBy = isGlobalReason(availability)
    ? signingNoticeId
    : hint
      ? signHintId
      : undefined;

  return (
    <div className="version-controls">
      <div className="file-actions">
        {signed &&
        canDownload &&
        version.file_id &&
        version.document_file_id ? (
          <Link
            className="button button--primary button--compact"
            to="/modules/document-workflow/documents/new"
            search={{
              signed_version_id: version.id,
              signed_file_id: version.document_file_id,
            }}
            {...label("Отправить")}
          >
            <HiOutlinePaperAirplane aria-hidden="true" />
            <span>Отправить</span>
          </Link>
        ) : null}
        {canOpen ? (
          <button
            className="button button--secondary button--compact"
            type="button"
            {...label("Открыть")}
            onClick={() => setPreviewOpen(true)}
          >
            <HiOutlineEye aria-hidden="true" />
            <span>Открыть</span>
          </button>
        ) : null}
        {canDownload ? (
          <button
            className="button button--secondary button--compact"
            type="button"
            disabled={download.isPending}
            {...label("Скачать")}
            onClick={() => download.mutate()}
          >
            <HiOutlineArrowDownTray aria-hidden="true" />
            <span>Скачать</span>
          </button>
        ) : null}
        <button
          className="button button--primary button--compact"
          type="button"
          disabled={!canStartSigning || signOpen}
          aria-describedby={describedBy}
          {...label("Подписать")}
          onClick={() => setSignOpen(true)}
        >
          <HiOutlinePencilSquare aria-hidden="true" />
          <span>Подписать</span>
        </button>
        <button
          className="button button--text button--compact"
          type="button"
          aria-expanded={signaturesOpen}
          aria-controls={signaturesId}
          {...label("Подписи")}
          onClick={() => setSignaturesOpen((open) => !open)}
        >
          <HiOutlineShieldCheck aria-hidden="true" />
          <span>Подписи</span>
        </button>
        {unlinkable ? (
          <button
            className="button button--text button--compact"
            type="button"
            disabled={unlink.isPending}
            {...label("Отвязать")}
            onClick={() => setConfirmUnlink(true)}
          >
            {`Отвязать${suffix}`}
          </button>
        ) : null}
      </div>
      {!scan.releasable ? (
        <p className="file-hint" data-blocked={version.scan_status}>
          {scan.blockedReason}
        </p>
      ) : null}
      {hint ? (
        <p id={signHintId} className="file-hint">
          {hint}
        </p>
      ) : null}
      {signed ? (
        <p className="file-hint">
          Подписанная версия неизменяема: её нельзя изменить, отвязать или
          удалить. Изменения выпускаются новой версией.
        </p>
      ) : null}
      {download.isError ? (
        <Notice>{downloadErrorMessage(download.error)}</Notice>
      ) : null}
      {unlink.isError ? (
        <Notice>{unlinkErrorMessage(unlink.error)}</Notice>
      ) : null}
      {confirmUnlink ? (
        <div
          className="file-confirm"
          role="alertdialog"
          aria-labelledby={unlinkTitleId}
        >
          <p id={unlinkTitleId}>
            Отвязать версию {version.version_number} от документа?
          </p>
          <p className="file-hint">
            Удаляется только эта связь. Сам файл остаётся, пока он используется
            в других местах — письме, обсуждении, других документах
            {version.reference_count > 1
              ? ` (сейчас связей: ${version.reference_count})`
              : ""}
            .
          </p>
          <div className="file-actions">
            <button
              className="button button--danger button--compact"
              type="button"
              disabled={unlink.isPending}
              onClick={() => unlink.mutate()}
            >
              {unlink.isPending ? "Отвязываем…" : "Отвязать версию"}
            </button>
            <button
              className="button button--text button--compact"
              type="button"
              disabled={unlink.isPending}
              onClick={() => setConfirmUnlink(false)}
            >
              Отмена
            </button>
          </div>
        </div>
      ) : null}
      {signOpen && availability.status === "available" && capabilities ? (
        <VersionSigningPanel
          services={services}
          version={version}
          profile={availability.profile}
          requestTtlSeconds={capabilities.request_ttl_seconds}
          onSigned={() => {
            // Подписанная версия меняет своё состояние в списках файлов и версий.
            void client.invalidateQueries({ queryKey: ["document-workflow"] });
          }}
          onClose={() => setSignOpen(false)}
        />
      ) : null}
      {signaturesOpen ? (
        <div id={signaturesId}>
          <VersionSignatures version={version} />
        </div>
      ) : null}
      {previewOpen ? (
        <SignedPreviewDialog
          title={displayName}
          mimeType={version.detected_mime ?? "application/octet-stream"}
          loadPreview={() =>
            services.documentWorkflow.previewVersion(version.id)
          }
          onClose={() => setPreviewOpen(false)}
        />
      ) : null}
    </div>
  );
}
